/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ActionTree } from "vuex";
import Product from "@/models/Product";
import type AssemblyGroup from "@/models/AssemblyGroup";
// define your typings for the store state
export interface AssemblyGroupProduct {
  id: string;
  product_id: string;
  max: number;
  note?: string;
}

export interface AssemblyConfig {
  id: string | null;
  name: string;
  slug?: string;
  isSetName: boolean;
}

export interface AssemblyProduct {
  id: number;
  product: Product | null;
  assemblyId: string | null;
  assemblyGroup: string;
  assemblyConfig: {
    id: string | null;
  };
  qty: number;
  total: number;
  isLast?: boolean;
  performance?: number | null;
}

export interface StateAssembly {
  version: number;
  assemblyGroups: Array<AssemblyGroup>;
  assemblyGroupConfigs: Array<AssemblyGroupProduct>;
  products: Array<AssemblyProduct>;
  lists: Array<Array<AssemblyProduct>>;
  assemblyConfigs: Array<AssemblyConfig>;
  selectedAssemblyConfigId: string | null;
  activeAssemblyGroup: AssemblyGroup | null;
  allCategoriesInSecondarySearch: boolean;
  secondarySearches: Array<string>;
  brands: Array<string>;
  performances: Array<number>;
  stockStatus: Array<{ code: string; value: string }>;
  loading: boolean;
}

export interface AssemblyConfigDetail {
  id: string;
  qty: number;
  product: Product;
  performance?: number;
  assemblyGroup: AssemblyGroup;
  assemblyConfig: AssemblyConfig;
  pivot?: {
    qty: number;
  };
}

export const defaultAssemblyInit: StateAssembly = {
  version: 1.6,
  assemblyGroupConfigs: [],
  assemblyGroups: [],
  products: [],
  lists: [],
  assemblyConfigs: [],
  selectedAssemblyConfigId: null,
  activeAssemblyGroup: null,
  allCategoriesInSecondarySearch: false,
  secondarySearches: [],
  brands: [],
  performances: [],
  stockStatus: [],
  loading: false,
};

const state = () => defaultAssemblyInit;

// mutations
const mutations = {
  setAssemblyGroups(
    state: StateAssembly,
    assemblyGroups: Array<AssemblyGroup>,
  ) {
    state.assemblyGroups = assemblyGroups;
  },
  setActiveAssemblyGroup(state: StateAssembly, assemblyGroup: AssemblyGroup) {
    state.activeAssemblyGroup = assemblyGroup;
    state.allCategoriesInSecondarySearch = false;
    state.secondarySearches = [];
  },
  setNextActiveAssemblyGroup(state: StateAssembly) {
    if (state.activeAssemblyGroup) {
      const groups = state.assemblyGroups
        .filter((group: AssemblyGroup) => {
          return (
            group.is_accessory == state.activeAssemblyGroup?.is_accessory ||
            !group.is_accessory == !state.activeAssemblyGroup?.is_accessory
          );
        })
        .sort((a: AssemblyGroup, b: AssemblyGroup) => a.order - b.order);
      const index = groups.findIndex((group: AssemblyGroup) => {
        return group.id == state.activeAssemblyGroup?.id;
      });
      if (index + 1 <= groups.length - 1) {
        state.activeAssemblyGroup = groups[index + 1] || null;
        state.allCategoriesInSecondarySearch = false;
        state.secondarySearches = [];
      }
    }
  },
  setPrevActiveAssemblyGroup(state: StateAssembly) {
    if (state.activeAssemblyGroup) {
      const groups = state.assemblyGroups
        .filter((group: AssemblyGroup) => {
          return (
            group.is_accessory == state.activeAssemblyGroup?.is_accessory ||
            !group.is_accessory == !state.activeAssemblyGroup?.is_accessory
          );
        })
        .sort((a: AssemblyGroup, b: AssemblyGroup) => a.order - b.order);
      const index = groups.findIndex((group: AssemblyGroup) => {
        return group.id == state.activeAssemblyGroup?.id;
      });
      if (index - 1 >= 0) {
        state.activeAssemblyGroup = groups[index - 1] || null;
        state.allCategoriesInSecondarySearch = false;
        state.secondarySearches = [];
      }
    }
  },
  setAllCategoriesInSecondarySearch(
    state: StateAssembly,
    allCategories: boolean,
  ) {
    state.allCategoriesInSecondarySearch = allCategories;
  },
  toggleAllCategoriesInSecondarySearch(state: StateAssembly) {
    state.allCategoriesInSecondarySearch =
      !state.allCategoriesInSecondarySearch;
  },
  addSecondarySearch(state: StateAssembly, secondaryText: string) {
    const exists = state.secondarySearches.find(
      (item) => item.toLowerCase() == secondaryText.toLowerCase(),
    );
    if (exists == undefined) {
      state.secondarySearches.push(secondaryText.toLowerCase());
    }
  },
  deleteSecondarySearch(state: StateAssembly, secondaryText: string) {
    state.secondarySearches = state.secondarySearches.filter(
      (item) => item != secondaryText,
    );
  },
  resetSecondarySearches(state: StateAssembly) {
    state.secondarySearches = [];
  },
  updateActiveAssemblyGroup(
    state: StateAssembly,
    assemblyGroup: AssemblyGroup,
  ) {
    const existAssemblyGroup = state.assemblyGroups.findIndex(
      (item: AssemblyGroup) => item.id == assemblyGroup.id,
    );
    if (existAssemblyGroup >= 0 && state.assemblyGroups[existAssemblyGroup]) {
      state.assemblyGroups[existAssemblyGroup].is_active =
        assemblyGroup.is_active;
    }
  },
  setSelectedAssemblyConfig(state: StateAssembly, assemblyConfigId: string) {
    state.selectedAssemblyConfigId = assemblyConfigId;
  },
  resetAssemblyConfig(state: StateAssembly) {
    state.selectedAssemblyConfigId = null;
  },
  resetNewAssemblyConfig(state: StateAssembly) {
    state.selectedAssemblyConfigId = null;
    const newAssemblyConfigIndex = state.assemblyConfigs.findIndex(
      (item: AssemblyConfig) => {
        return item.id == null;
      },
    );
    if (
      newAssemblyConfigIndex >= 0 &&
      state.assemblyConfigs[newAssemblyConfigIndex]
    ) {
      state.assemblyConfigs[newAssemblyConfigIndex].name = "Nuevo ensamble";
      state.assemblyConfigs[newAssemblyConfigIndex].id = null;
      state.assemblyConfigs[newAssemblyConfigIndex].slug = "";
      state.assemblyConfigs[newAssemblyConfigIndex].isSetName = false;
      state.products = state.products.filter((item: AssemblyProduct) => {
        return item.assemblyConfig.id != null;
      });
    }
  },
  setSelectedAssemblyConfigName(
    state: StateAssembly,
    assemblyConfigName: string,
  ) {
    const index = state.assemblyConfigs.findIndex((item: AssemblyConfig) => {
      return item.id == state.selectedAssemblyConfigId;
    });
    if (index < 0 && state.selectedAssemblyConfigId == null) {
      state.assemblyConfigs.push({
        id: null,
        name: assemblyConfigName,
        isSetName: true,
      });
    } else if (state.assemblyConfigs[index]) {
      state.assemblyConfigs[index].name = assemblyConfigName;
      state.assemblyConfigs[index].isSetName = true;
    }
  },
  setAssemblyConfigs(
    state: StateAssembly,
    assemblyConfigs: Array<AssemblyConfig>,
  ) {
    const newAssemblyConfig = state.assemblyConfigs.find(
      (item: AssemblyConfig) => {
        return item.id == null;
      },
    );
    state.assemblyConfigs = assemblyConfigs;
    if (newAssemblyConfig) {
      state.assemblyConfigs.push(newAssemblyConfig);
    } else {
      state.assemblyConfigs.push({
        id: null,
        name: "Nuevo ensamble",
        isSetName: false,
      });
    }
  },
  pushAssemblyConfigs(state: StateAssembly, assemblyConfig: AssemblyConfig) {
    const index = state.assemblyConfigs.findIndex((item: AssemblyConfig) => {
      return item.id == assemblyConfig.id;
    });
    if (index >= 0) {
      state.assemblyConfigs[index] = assemblyConfig;
    } else {
      state.assemblyConfigs.push(assemblyConfig);
    }
    state.assemblyConfigs = state.assemblyConfigs.sort(
      (a: AssemblyConfig, b: AssemblyConfig) => {
        if (a.id === null) return 1;
        if (b.id === null) return -1;
        return parseInt(a.id) - parseInt(b.id);
      },
    );
  },
  addProduct(state: StateAssembly, product: AssemblyProduct) {
    product.assemblyConfig = { id: state.selectedAssemblyConfigId };
    if (product.product) {
      const newProductModel = new Product(product.product);
      product.total = newProductModel.getFinalCurrentPrice() * product.qty;
    }
    state.products.push(product);
  },
  addOmitedProduct(state: StateAssembly, assemblyConfigDetail: any) {
    const product: AssemblyProduct = {
      id: assemblyConfigDetail.id,
      product: null,
      assemblyId: assemblyConfigDetail.assemblyConfig.id,
      assemblyGroup: assemblyConfigDetail.assemblyGroup.id,
      assemblyConfig: assemblyConfigDetail.assemblyConfig,
      qty: 0,
      total: 0,
      isLast: false,
      performance: null,
    };
    state.products.push(product);
  },
  removeOmitedProduct(state: StateAssembly, assemblyGroupId: string) {
    state.products = state.products.filter(
      (item: AssemblyProduct) =>
        !(
          !item.product &&
          item.assemblyConfig.id == state.selectedAssemblyConfigId &&
          item.assemblyGroup == assemblyGroupId
        ),
    );
  },
  updateProduct(
    state: StateAssembly,
    product: { id: string; qty: number; assemblyGroup: string },
  ) {
    const existProduct = state.products.findIndex(
      (item: AssemblyProduct) =>
        item.product?.id == product.id &&
        item.assemblyConfig.id == state.selectedAssemblyConfigId,
    );
    if (existProduct >= 0 && state.products[existProduct]) {
      const newProductModel = state.products[existProduct].product
        ? new Product(state.products[existProduct].product)
        : null;
      state.products[existProduct].qty = product.qty;
      if (product.assemblyGroup) {
        state.products[existProduct].assemblyGroup = product.assemblyGroup;
      }
      state.products[existProduct].total =
        (newProductModel ? newProductModel.getFinalCurrentPrice() : 0) *
        state.products[existProduct].qty;
      if (state.products[existProduct].qty == 0) {
        state.products = state.products.filter(
          (item: AssemblyProduct) => item.product?.id != product.id,
        );
        state.assemblyGroupConfigs = state.assemblyGroupConfigs.filter(
          (item: AssemblyGroupProduct) => item.product_id != product.id,
        );
      }
    }
  },
  updateInfoProduct(
    state: StateAssembly,
    product: { id: string; product: Product },
  ) {
    const existProduct = state.products.findIndex(
      (item: AssemblyProduct) =>
        item.product?.id == product.id &&
        item.assemblyConfig.id == state.selectedAssemblyConfigId,
    );
    if (existProduct >= 0 && state.products[existProduct]) {
      state.products[existProduct].product = product.product;
    }
  },
  deleteProduct(state: StateAssembly, product: AssemblyProduct) {
    const productsOtherAssemblyConfig = state.products.filter(
      (item: AssemblyProduct) =>
        item.assemblyConfig.id != state.selectedAssemblyConfigId,
    );
    const productsSameAssemblyConfig = state.products.filter(
      (item: AssemblyProduct) =>
        !(
          item.product?.id == product.product?.id &&
          item.assemblyGroup == product.assemblyGroup
        ) && item.assemblyConfig.id == state.selectedAssemblyConfigId,
    );
    state.products = [
      ...productsOtherAssemblyConfig,
      ...productsSameAssemblyConfig,
    ];
    const existSameProductId = state.products.findIndex(
      (item: AssemblyProduct) => item.product?.id == product.product?.id,
    );
    if (existSameProductId == -1) {
      state.assemblyGroupConfigs = state.assemblyGroupConfigs.filter(
        (item: AssemblyGroupProduct) => item.product_id != product.product?.id,
      );
    }
  },
  deleteProductsByAssemblyGroup(state: StateAssembly, assemblyGroupId: string) {
    const productsOtherAssemblyConfig = state.products.filter(
      (item: AssemblyProduct) =>
        item.assemblyConfig.id != state.selectedAssemblyConfigId,
    );
    const productsSameAssemblyConfig = state.products.filter(
      (item: AssemblyProduct) =>
        item.assemblyGroup != assemblyGroupId &&
        item.assemblyConfig.id == state.selectedAssemblyConfigId,
    );
    state.products = [
      ...productsOtherAssemblyConfig,
      ...productsSameAssemblyConfig,
    ];
    // const existSameProductId = state.products.findIndex(
    //   (item: AssemblyProduct) => item.product.id == productId,
    // );
    // if (existSameProductId == -1) {
    //   state.assemblyGroupConfigs = state.assemblyGroupConfigs.filter(
    //     (item: AssemblyGroupProduct) => item.product_id != productId,
    //   );
    // }
  },
  resetProducts(state: StateAssembly) {
    state.products = state.products.filter(
      (item: AssemblyProduct) =>
        item.assemblyConfig.id != state.selectedAssemblyConfigId,
    );
  },
  resetLists(state: StateAssembly) {
    state.lists = [];
  },
  saveList(state: StateAssembly) {
    const newAssembly = state.products.filter(
      (item: AssemblyProduct) => item.assemblyConfig.id == null,
    );
    state.lists.push(newAssembly);
  },
  updateAssemblyGroup(
    state: StateAssembly,
    assemblyGroup: AssemblyGroupProduct,
  ) {
    const existGroupByProduct = state.assemblyGroupConfigs.findIndex(
      (group: AssemblyGroupProduct) =>
        group.product_id == assemblyGroup.product_id &&
        group.id == assemblyGroup.id,
    );
    if (existGroupByProduct == -1) {
      state.assemblyGroupConfigs.push(assemblyGroup);
    } else if (state.assemblyGroupConfigs[existGroupByProduct]) {
      state.assemblyGroupConfigs[existGroupByProduct].max = assemblyGroup.max;
      state.assemblyGroupConfigs[existGroupByProduct].note = assemblyGroup.note;
    }
  },
  setAssemblyId(state: StateAssembly, assemblyId: string) {
    state.products
      .filter(
        (item: AssemblyProduct) =>
          item.assemblyConfig.id == state.selectedAssemblyConfigId,
      )
      .forEach((item: AssemblyProduct) => {
        return (item.assemblyId = assemblyId);
      });
  },
  setBrands(state: StateAssembly, brandCode: string) {
    if (state.brands.includes(brandCode)) {
      state.brands = state.brands.filter((item) => item !== brandCode);
    } else {
      state.brands.push(brandCode);
    }
  },
  setPerformances(state: StateAssembly, performanceId: number) {
    if (state.performances.includes(performanceId)) {
      state.performances = state.performances.filter(
        (item) => item !== performanceId,
      );
    } else {
      state.performances.push(performanceId);
    }
  },
  setStockStatus(
    state: StateAssembly,
    stockStatus: { code: string; value: string },
  ) {
    if (
      state.stockStatus.find(
        (item: { code: string }) => item.code == stockStatus.code,
      )
    ) {
      state.stockStatus = state.stockStatus.filter(
        (item: { code: string }) => item.code !== stockStatus.code,
      );
    } else {
      state.stockStatus.push(stockStatus);
    }
  },
  resetBrands(state: StateAssembly) {
    state.brands = [];
  },
  resetPerformances(state: StateAssembly) {
    state.performances = [];
  },
  resetStockStatus(state: StateAssembly) {
    state.stockStatus = [];
  },
  setLoading(state: StateAssembly, loading: boolean) {
    return (state.loading = loading);
  },
};

const getters = {
  getAssemblyGroups(state: StateAssembly) {
    return state.assemblyGroups;
  },
  getActiveAssemblyGroup(state: StateAssembly) {
    return state.activeAssemblyGroup;
  },
  getNextActiveAssemblyGroup(state: StateAssembly, getters: any) {
    let index = null;
    const groups = state.assemblyGroups
      .filter((group: AssemblyGroup) => {
        return (
          group.is_accessory == state.activeAssemblyGroup?.is_accessory ||
          !group.is_accessory == !state.activeAssemblyGroup?.is_accessory
        );
      })
      .sort((a: AssemblyGroup, b: AssemblyGroup) => a.order - b.order);
    if (state.activeAssemblyGroup) {
      index = groups.findIndex((group: AssemblyGroup) => {
        return group.id == state.activeAssemblyGroup?.id;
      });
    } else {
      index = groups.findIndex((group: AssemblyGroup) => {
        return (
          getters.getProductsWithOmitted.filter((product: AssemblyProduct) => {
            return product.assemblyGroup == group.id;
          }).length == 0
        );
      });
      index--;
      // index >= 0 ? index-- : -1;
    }
    if (index + 1 <= groups.length - 1) {
      return groups[index + 1];
    }
    return null;
  },
  getPrevActiveAssemblyGroup(state: StateAssembly, getters: any) {
    let index = null;
    const groups = state.assemblyGroups
      .filter((group: AssemblyGroup) => {
        return (
          group.is_accessory == state.activeAssemblyGroup?.is_accessory ||
          !group.is_accessory == !state.activeAssemblyGroup?.is_accessory
        );
      })
      .sort((a: AssemblyGroup, b: AssemblyGroup) => a.order - b.order);
    if (state.activeAssemblyGroup) {
      index = groups.findIndex((group: AssemblyGroup) => {
        return group.id == state.activeAssemblyGroup?.id;
      });
    } else {
      index = groups.findIndex((group: AssemblyGroup) => {
        return (
          getters.getProducts.filter((product: AssemblyProduct) => {
            return product.assemblyGroup == group.id;
          }).length == 0
        );
      });
      if (index == -1) index = groups.length;
    }
    if (index - 1 >= 0) {
      return groups[index - 1];
    }
    return null;
  },
  getAllCategoriesInSecondarySearch(state: StateAssembly) {
    return state.allCategoriesInSecondarySearch;
  },
  getSecondarySearches(state: StateAssembly) {
    return state.secondarySearches;
  },
  getTotalComponents(state: StateAssembly) {
    return state.assemblyGroups.filter(
      (group: AssemblyGroup) => !group.is_accessory,
    ).length;
  },
  getTotalAccessories(state: StateAssembly) {
    return state.assemblyGroups.filter(
      (group: AssemblyGroup) => group.is_accessory,
    ).length;
  },
  getSelectedAssemblyConfigId(state: StateAssembly) {
    return state.selectedAssemblyConfigId;
  },
  getSelectedAssemblyConfig(state: StateAssembly) {
    if (state.assemblyConfigs.length > 0) {
      const exist = state.assemblyConfigs.find((item: AssemblyConfig) => {
        return item.id == state.selectedAssemblyConfigId;
      });
      if (exist != undefined) {
        return exist;
      }
    }
    return {
      id: null,
      name: "",
    };
  },
  getAssemblyConfigs(state: StateAssembly) {
    return state.assemblyConfigs;
  },
  getProductsWithOmitted(state: StateAssembly) {
    return state.products
      .filter(
        (item: AssemblyProduct) =>
          item.assemblyConfig.id == state.selectedAssemblyConfigId,
      )
      .map((item: AssemblyProduct, index: number, arr: AssemblyProduct[]) => ({
        ...item,
        isLast: index === arr.length - 1,
      }));
  },
  getProducts(state: StateAssembly) {
    return state.products
      .filter((item: AssemblyProduct) => item.product)
      .filter(
        (item: AssemblyProduct) =>
          item.assemblyConfig.id == state.selectedAssemblyConfigId,
      )
      .map((item: AssemblyProduct, index: number, arr: AssemblyProduct[]) => ({
        ...item,
        isLast: index === arr.length - 1,
      }));
  },
  getAssemblyGroupsBySelectedProducts(_state: StateAssembly, getters: any) {
    return getters.getProducts.map((product: AssemblyProduct) => {
      return +product.assemblyGroup;
    });
  },
  getGroupWithLastProduct(_state: StateAssembly, getters: any) {
    const product = getters.getProducts.find((item: AssemblyProduct) => {
      return item?.isLast;
    });
    return product?.assemblyGroup;
  },
  getFormatedOmittedProducts(state: StateAssembly, _getters: any) {
    const formattedProducts = [];
    const products = state.products
      .filter((item: AssemblyProduct) => !item.product)
      .filter(
        (item: AssemblyProduct) =>
          item.assemblyConfig.id == state.selectedAssemblyConfigId,
      )
      .map((item: AssemblyProduct, index: number, arr: AssemblyProduct[]) => ({
        ...item,
        isLast: index === arr.length - 1,
      }));
    for (let index = 0; index < products.length; index++) {
      const element = {
        product_id: null,
        qty: 0,
        assembly_group_id: products[index]?.assemblyGroup,
      };
      formattedProducts.push(element);
    }

    return formattedProducts;
  },
  getFormatedProducts(_state: StateAssembly, getters: any) {
    const formattedProducts = [];
    const products = getters.getProducts;
    for (let index = 0; index < products.length; index++) {
      const element = {
        product_id: products[index].product.id,
        qty: products[index].qty,
        assembly_group_id: products[index].assemblyGroup,
      };
      formattedProducts.push(element);
    }

    return formattedProducts;
  },
  getFormatedProductsToLogin(state: StateAssembly, _getters: any) {
    const formattedProducts = [];
    const products = state.products;
    for (let index = 0; index < products.length; index++) {
      if (!products[index]?.id) {
        const element = {
          assembly_config_id: products[index]?.assemblyConfig.id,
          assembly_group_id: products[index]?.assemblyGroup,
          product_id: products[index]?.product?.id,
          qty: products[index]?.qty,
        };
        formattedProducts.push(element);
      }
    }

    return formattedProducts;
  },
  getIdProducts(_state: StateAssembly, getters: any) {
    const idProducts = [];
    const products = getters.getProducts;
    for (let index = 0; index < products.length; index++) {
      idProducts.push(products[index].product.id);
    }

    return idProducts;
  },
  getProductsByAssemblyGroup:
    (state: StateAssembly) => (assemblyGroupId: string) => {
      return state.products
        .filter(
          (item: AssemblyProduct) =>
            item.assemblyConfig.id == state.selectedAssemblyConfigId,
        )
        .filter((product: AssemblyProduct) => {
          return product.assemblyGroup == assemblyGroupId;
        });
    },
  hasProductsByAssemblyGroup:
    (state: StateAssembly) => (assemblyGroupId: string) => {
      const products = state.products
        .filter(
          (item: AssemblyProduct) =>
            item.assemblyConfig.id == state.selectedAssemblyConfigId,
        )
        .filter((product: AssemblyProduct) => {
          return product.assemblyGroup == assemblyGroupId;
        });
      return products.length > 0;
    },
  hasOmitedProductsByAssemblyGroup:
    (state: StateAssembly) => (assemblyGroupId: string) => {
      const products = state.products
        .filter((item: AssemblyProduct) => !item.product)
        .filter(
          (item: AssemblyProduct) =>
            item.assemblyConfig.id == state.selectedAssemblyConfigId,
        )
        .filter((product: AssemblyProduct) => {
          return product.assemblyGroup == assemblyGroupId;
        });
      return products.length > 0;
    },
  getTotalAssemblyComponentsSelected(state: StateAssembly, getters: any) {
    const groupsWithProducts = getters.getProducts
      .map((product: AssemblyProduct) => {
        return product.assemblyGroup;
      })
      .filter((assemblyGroup: string) => {
        const find = state.assemblyGroups.find((item: AssemblyGroup) => {
          return item.id == assemblyGroup;
        });
        return find && !find.is_accessory;
      });
    const uniqueGroupsWithProducts = [...new Set(groupsWithProducts)];
    return uniqueGroupsWithProducts.length;
  },
  getTotalAssemblyAccessoriesSelected(state: StateAssembly, getters: any) {
    const groupsWithProducts = getters.getProducts
      .map((product: AssemblyProduct) => {
        return product.assemblyGroup;
      })
      .filter((assemblyGroupId: string) => {
        const find = state.assemblyGroups.find((item: AssemblyGroup) => {
          return item.id == assemblyGroupId;
        });
        return find && find.is_accessory;
      });
    const uniqueGroupsWithProducts = [...new Set(groupsWithProducts)];
    return uniqueGroupsWithProducts.length;
  },
  getTotal(_state: StateAssembly, getters: any) {
    let total = 0;
    getters.getProducts.forEach((product: AssemblyProduct) => {
      total += product.total * 1;
    });
    return total;
  },
  getMaxByAssemblyGroup:
    (state: StateAssembly, getters: any) => (assemblyGroupId: string) => {
      const products = getters.getProducts;
      return state.assemblyGroupConfigs
        .filter((item: AssemblyGroupProduct) => {
          return (
            products.findIndex((product: AssemblyProduct) => {
              return product.product?.id == item.product_id;
            }) >= 0
          );
        })
        .filter((item: AssemblyGroupProduct) => {
          return item.id == assemblyGroupId;
        })
        .reduce((max: number, item: AssemblyGroupProduct) => {
          return item.max > max ? item.max : max;
        }, 1);
    },
  getNotesByAssemblyGroup:
    (state: StateAssembly, getters: any) => (assemblyGroupId: string) => {
      const products = getters.getProducts;
      return state.assemblyGroupConfigs
        .filter((item: AssemblyGroupProduct) => {
          return (
            products.findIndex((product: AssemblyProduct) => {
              return product.product?.id == item.product_id;
            }) >= 0
          );
        })
        .filter((item: AssemblyGroupProduct) => {
          return item.id == assemblyGroupId && item.note != "";
        });
    },
  getTotalByAssemblyGroup:
    (_state: StateAssembly, getters: any) => (assemblyGroupId: string) => {
      return getters.getProducts
        .filter((item: AssemblyProduct) => {
          return item.assemblyGroup == assemblyGroupId;
        })
        .reduce((total: number, item: AssemblyProduct) => {
          return total + item.qty;
        }, 0);
    },
  isAssemblyCompleted(state: StateAssembly, getters: any) {
    let isCompleted = true;
    state.assemblyGroups
      .filter((item: AssemblyGroup) => {
        return item.is_required;
      })
      .forEach((item: AssemblyGroup) => {
        const products = getters.getProducts.filter(
          (product: AssemblyProduct) => {
            return product.assemblyGroup == item.id;
          },
        );
        if (products.length <= 0) {
          isCompleted = false;
        }
      });
    return isCompleted;
  },
  findAssemblyGroupByCategory:
    (state: StateAssembly) => (categoryId: string) => {
      return state.assemblyGroups?.find((group: AssemblyGroup) => {
        const find = group.categories?.find((category: any) => {
          return category.id == categoryId;
        });
        return find != undefined;
      });
    },
  getBrands(state: StateAssembly) {
    return state.brands;
  },
  getPerformances(state: StateAssembly) {
    return state.performances;
  },
  getStockStatus(state: StateAssembly) {
    return state.stockStatus;
  },
  getPendingAssemblyGroups(state: StateAssembly, getters: any) {
    if (state.activeAssemblyGroup) {
      const groups = state.assemblyGroups
        .filter((group: AssemblyGroup) => {
          return (
            group.is_accessory == state.activeAssemblyGroup?.is_accessory ||
            !group.is_accessory == !state.activeAssemblyGroup?.is_accessory
          );
        })
        .sort((a: AssemblyGroup, b: AssemblyGroup) => a.order - b.order);
      let indexActiveAssemblyGroup = groups.findIndex(
        (group: AssemblyGroup) => {
          return group.id == state.activeAssemblyGroup?.id;
        },
      );
      indexActiveAssemblyGroup--;
      const pendingGroups = [];
      if (indexActiveAssemblyGroup < 0) return [];
      for (let index = 0; index <= indexActiveAssemblyGroup; index++) {
        if (!groups[index]?.is_required) continue;
        const hasProduct = getters.hasProductsByAssemblyGroup(
          groups[index]?.id,
        );
        if (!hasProduct) {
          pendingGroups.push(groups[index]);
        }
      }
      return pendingGroups;
    }
    return [];
  },
  getLoading(state: StateAssembly) {
    return state.loading;
  },
};

const actions: ActionTree<StateAssembly, any> = {
  addProduct({ getters, commit }, newProduct: AssemblyProduct) {
    const max = getters.getMaxByAssemblyGroup(newProduct.assemblyGroup);
    const total = getters.getTotalByAssemblyGroup(newProduct.assemblyGroup);
    const existProduct = getters.getProducts.find(
      (item: AssemblyProduct) =>
        item.product?.id == newProduct.product?.id &&
        newProduct.assemblyGroup == item.assemblyGroup,
    );
    if (max >= total + 1) {
      if (existProduct == undefined) {
        newProduct.qty = 1;
        newProduct.total =
          (newProduct.product
            ? new Product(newProduct.product).getFinalCurrentPrice()
            : 0) * newProduct.qty;
        commit("addProduct", newProduct);
      } else {
        commit("updateProduct", {
          id: newProduct.product?.id,
          assemblyGroup: newProduct.assemblyGroup,
          qty: existProduct.qty + 1,
        });
      }
    }
  },
  replaceProduct({ getters, commit }, newProduct: AssemblyProduct) {
    const existProduct = getters.getProducts.find(
      (item: AssemblyProduct) => item.product?.id == newProduct.product?.id,
    );

    commit("deleteProductsByAssemblyGroup", newProduct.assemblyGroup);

    if (existProduct == undefined) {
      newProduct.qty = 1;
      newProduct.total =
        (newProduct.product ? newProduct.product.getFinalCurrentPrice() : 0) *
        newProduct.qty;
      commit("addProduct", newProduct);
    } else {
      commit("updateProduct", {
        id: newProduct.product?.id,
        assemblyGroup: newProduct.assemblyGroup,
        qty: existProduct.qty + 1,
      });
    }
  },
  addProducts({ commit, state }, products: Array<AssemblyConfigDetail>) {
    commit("resetProducts");
    products.forEach((detail: AssemblyConfigDetail, index: number) => {
      let newProductModel = null;
      if (detail.product) {
        newProductModel = new Product(detail.product);
        detail.product.performance = detail?.performance
          ? detail.performance
          : detail.product.performance;
      }
      let assemblyGroup = undefined;
      if (detail.assemblyGroup) {
        assemblyGroup = detail.assemblyGroup;
      }
      // else if (detail.product && detail.product.category) {
      //   assemblyGroup = getters.findAssemblyGroupByCategory(
      //     detail.product.category.id,
      //   );
      // }

      const newProduct = {
        id: detail.id,
        product: detail.product,
        assemblyGroup: assemblyGroup != undefined ? assemblyGroup?.id : null,
        assemblyConfig: {
          id: state.selectedAssemblyConfigId,
        },
        qty: detail.qty,
        total:
          detail.qty *
          (newProductModel ? newProductModel.getFinalCurrentPrice() : 0),
      };
      commit("addProduct", newProduct);
      if (
        detail.assemblyConfig &&
        index == 0 &&
        detail.assemblyConfig.id != state.selectedAssemblyConfigId
      ) {
        commit("setSelectedAssemblyConfig", detail.assemblyConfig.id);
      }
    });
  },
  addProductsFromCart({ getters, commit, state }, products: Array<unknown>) {
    commit("resetProducts");
    products.forEach((detail: any, index: number) => {
      const newProductModel = new Product(detail.product);
      let assemblyGroup = detail.assembly_group_id;
      if (assemblyGroup == null) {
        assemblyGroup = getters.findAssemblyGroupByCategory(
          detail.product.category_id,
        );
        assemblyGroup = assemblyGroup != undefined ? assemblyGroup.id : null;
      }
      const newProduct = {
        product: detail.product,
        assemblyGroup: assemblyGroup,
        assemblyConfig: {
          id: state.selectedAssemblyConfigId,
        },
        assemblyId: detail.assembly_id,
        qty: detail.qty_by_assembly,
        total: detail.qty_by_assembly * newProductModel.getFinalCurrentPrice(),
      };
      commit("addProduct", newProduct);
      if (
        detail.assemblyConfig &&
        index == 0 &&
        detail.assemblyConfig.id != state.selectedAssemblyConfigId
      ) {
        commit("setSelectedAssemblyConfig", detail.assemblyConfig.id);
      }
    });
  },
  deleteProduct({ commit }, product: AssemblyProduct) {
    commit("deleteProduct", product);
    commit("setPrevActiveAssemblyGroup");
  },
  updateQtyProduct(
    { getters, commit },
    product: { assemblyGroupId: string; productId: string; qty: number },
  ) {
    const max = getters.getMaxByAssemblyGroup(product.assemblyGroupId);
    const total = getters.getTotalByAssemblyGroup(product.assemblyGroupId);
    const existProduct = getters.getProducts.find(
      (item: AssemblyProduct) => item.product?.id == product.productId,
    );
    if (
      existProduct != undefined &&
      max >= total + product.qty - existProduct.qty
    ) {
      commit("updateProduct", {
        id: product.productId,
        assemblyGroup: product.assemblyGroupId,
        qty: product.qty,
      });
    }
  },
  updateAssemblyGroups(
    { commit },
    assemblyGroups: Array<AssemblyGroupProduct>,
  ) {
    assemblyGroups.forEach((assemblyGroup) => {
      commit("updateAssemblyGroup", {
        id: assemblyGroup.id,
        product_id: assemblyGroup.product_id,
        max: assemblyGroup.max,
        note: assemblyGroup.note,
      });
    });
  },
  setAssemblyGroups({ commit }, assemblyGroups: Array<AssemblyGroupProduct>) {
    commit("setAssemblyGroups", assemblyGroups);
    // const withoutAssemblyGroup = state.products.filter(
    //   (item: AssemblyProduct) => item.assemblyGroup == null && item.product,
    // );
    // if (withoutAssemblyGroup.length > 0) {
    //   withoutAssemblyGroup.forEach((product: AssemblyProduct) => {
    //     let assemblyGroup = undefined;
    //     if (product.product && product.product.category) {
    //       assemblyGroup = getters.findAssemblyGroupByCategory(
    //         product.product.category.id,
    //       );
    //     }
    //     commit("updateProduct", {
    //       id: product.product?.id,
    //       assemblyGroup: assemblyGroup != undefined ? assemblyGroup.id : null,
    //       qty: product.qty,
    //     });
    //   });
    // }
  },
  duplicateAssemblyConfig({ commit, getters }) {
    const selectedAssemblyConfig = getters.getSelectedAssemblyConfig;
    const newName: string = selectedAssemblyConfig
      ? selectedAssemblyConfig.name + " - copy"
      : "copy";
    const products: Array<AssemblyProduct> = getters.getProducts;
    commit("resetAssemblyConfig");
    commit("setSelectedAssemblyConfig", null);
    commit("setSelectedAssemblyConfigName", newName);
    commit("resetLists");

    products.forEach((product) => {
      const newProduct = { ...product };
      newProduct.assemblyConfig = { id: null };
      commit("addProduct", newProduct);
    });
  },
  setNextActiveAssemblyGroupByProduct({ state, getters }, productId: string) {
    const product = getters.getProducts.find((item: AssemblyProduct) => {
      return item.product?.id == productId;
    });
    const assemblyGroup = state.assemblyGroups.find((item: AssemblyGroup) => {
      return item.id == product.assemblyGroup;
    });
    if (assemblyGroup) {
      const groups = state.assemblyGroups.filter((group: AssemblyGroup) => {
        return (
          group.is_accessory == assemblyGroup?.is_accessory ||
          !group.is_accessory == !assemblyGroup?.is_accessory
        );
      });
      const index = groups.findIndex((group: AssemblyGroup) => {
        return group.id == assemblyGroup?.id;
      });
      if (index + 1 <= groups.length - 1) {
        state.activeAssemblyGroup = groups[index + 1] || null;
        state.allCategoriesInSecondarySearch = false;
        state.secondarySearches = [];
      }
    }
  },
};

export const assembly = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
